import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchObituaries } from '../../utils/api';
import useFavorites from '../../hooks/useFavorites';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import ErrorState from '../../components/common/ErrorState';
import { CardGridSkeleton } from '../../components/common/Skeleton';
import { ToastContext } from '../../context/ToastContext';
import { Heart, Trash2, Search } from 'lucide-react';

// 발인일 기준 진행상태/D-day
const getDerivedStatus = (departureDate) => {
  if (!departureDate) return 'active';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dep = new Date(departureDate);
  dep.setHours(0, 0, 0, 0);
  return dep < today ? 'ended' : 'active';
};

const getDday = (departureDate) => {
  if (!departureDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dep = new Date(departureDate);
  dep.setHours(0, 0, 0, 0);
  const diff = Math.round((dep - today) / (1000 * 60 * 60 * 24));
  if (diff > 0) return `D-${diff}`;
  if (diff === 0) return 'D-DAY';
  return null;
};

const FavoritesPage = () => {
  const navigate = useNavigate();
  const { favorites, removeFavorite } = useFavorites();
  const { showToast } = React.useContext(ToastContext);

  const [allObituaries, setAllObituaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('departure'); // departure | name
  const [removeTarget, setRemoveTarget] = useState(null);

  const loadObituaries = useCallback(async () => {
    try {
      setLoading(true);
      setIsError(false);
      const data = await fetchObituaries();
      setAllObituaries(data);
    } catch (error) {
      setIsError(true);
      showToast('즐겨찾기 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadObituaries();
  }, [loadObituaries]);

  // 즐겨찾기 ID 필터 (favorites 변경 시 즉시 반영 → 낙관적 업데이트, 깜빡임 없음)
  const favoriteObituaries = useMemo(() => {
    let list = allObituaries.filter((obit) => favorites.includes(obit.id));

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (o) =>
          o.deceasedName.toLowerCase().includes(term) ||
          (o.funeralHome || '').toLowerCase().includes(term)
      );
    }

    list = [...list].sort((a, b) => {
      if (sortBy === 'name')
        return a.deceasedName.localeCompare(b.deceasedName, 'ko');
      return new Date(a.departureDate) - new Date(b.departureDate);
    });

    return list;
  }, [allObituaries, favorites, searchTerm, sortBy]);

  const handleView = (id) => navigate(`/obituaries/${id}`);

  const confirmRemove = () => {
    if (removeTarget == null) return;
    removeFavorite(removeTarget); // 낙관적 업데이트
    setRemoveTarget(null);
    showToast('즐겨찾기에서 제거되었습니다.', 'success');
  };

  if (loading) {
    return (
      <Card>
        <div className="p-6">
          <h2 className="mb-6 text-xl font-bold text-primary">즐겨찾기</h2>
          <CardGridSkeleton count={4} columns={2} withImage={false} />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div className="p-6">
          <ErrorState
            title="즐겨찾기를 불러오지 못했습니다"
            message="네트워크 상태를 확인한 후 다시 시도해주세요."
            onRetry={loadObituaries}
          />
        </div>
      </Card>
    );
  }

  const hasFavorites = favorites.length > 0;

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-primary">즐겨찾기</h2>
          <p className="text-sm text-accent-600">
            총 {favoriteObituaries.length}개
          </p>
        </div>

        {!hasFavorites ? (
          <div className="text-center py-12">
            <Heart className="w-16 h-16 mx-auto text-accent-300 mb-4" />
            <p className="text-accent-600 mb-4">즐겨찾기한 부고가 없습니다.</p>
            <Button variant="outline" onClick={() => navigate('/obituaries')}>
              부고 둘러보기
            </Button>
          </div>
        ) : (
          <>
            {/* 검색 + 정렬 */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-400"
                  size={18}
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="고인명/장례식장 검색..."
                  aria-label="즐겨찾기 검색"
                  className="w-full rounded-lg border border-accent-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="정렬 기준"
                className="rounded-lg border border-accent-300 bg-white px-3 py-2 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="departure">발인일순</option>
                <option value="name">이름순</option>
              </select>
            </div>

            {favoriteObituaries.length === 0 ? (
              <p className="rounded-lg bg-accent-50 px-4 py-10 text-center text-sm text-accent-600">
                검색 결과가 없습니다.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {favoriteObituaries.map((obituary) => {
                  const status = getDerivedStatus(obituary.departureDate);
                  const dday = getDday(obituary.departureDate);
                  return (
                    <div
                      key={obituary.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleView(obituary.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleView(obituary.id);
                        }
                      }}
                      className="cursor-pointer rounded-lg border border-accent-200 p-4 transition-colors hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <div className="mb-3 flex items-start justify-between">
                        <div className="flex-1">
                          <div className="mb-1 flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-primary">
                              {obituary.deceasedName}
                            </h3>
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                status === 'ended'
                                  ? 'bg-gray-100 text-gray-700'
                                  : 'bg-green-100 text-green-800'
                              }`}
                            >
                              {status === 'ended' ? '종료' : '진행중'}
                            </span>
                            {dday && (
                              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-bold text-primary-700">
                                {dday}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-accent-600">
                            {obituary.funeralHome}
                          </p>
                          <p className="text-sm text-accent-600">
                            {obituary.funeralRoom}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRemoveTarget(obituary.id);
                          }}
                          className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                          aria-label="즐겨찾기 제거"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>

                      <div className="text-xs text-accent-500">
                        발인:{' '}
                        {obituary.departureDate
                          ? new Date(
                              obituary.departureDate
                            ).toLocaleDateString('ko-KR')
                          : '-'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* 제거 확인 모달 */}
      <ConfirmModal
        isOpen={removeTarget != null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        title="즐겨찾기 제거"
        message="이 부고를 즐겨찾기에서 제거하시겠습니까?"
        confirmLabel="제거"
        confirmVariant="danger"
      />
    </Card>
  );
};

export default FavoritesPage;
